/**
 * Server-Sent Events (SSE) Transport Implementation
 *
 * One-way server-to-client streaming via HTTP with automatic reconnection.
 * Ideal for server-driven updates and real-time dashboards.
 */

import { BaseTransport } from './BaseTransport';
import type { TransportConfig } from '../types';
import { calculateRetryDelay, delay } from '../utils';

export class SseTransport extends BaseTransport {
  private eventSource: EventSource | null = null;

  private reconnectAttempt = 0;

  constructor(id: string, config: TransportConfig) {
    super(id, 'sse', config);
  }

  async connect(): Promise<void> {
    if (this.eventSource && this.eventSource.readyState === EventSource.OPEN) {
      return;
    }

    this.setStatus('connecting');

    try {
      const url = new URL(this.config.url);
      if (this.config.queryParams) {
        for (const [key, value] of Object.entries(this.config.queryParams)) {
          url.searchParams.set(key, value);
        }
      }

      this.eventSource = new EventSource(url.toString());

      // Handle incoming messages
      this.eventSource.addEventListener('message', (event: MessageEvent) => {
        this.emitMessage(event.data);
      });

      // Handle custom event types
      this.eventSource.addEventListener('data', (event: MessageEvent) => {
        this.emitMessage(event.data);
      });

      // Handle errors
      this.eventSource.addEventListener('error', () => {
        const state = this.eventSource?.readyState;
        if (state === EventSource.CLOSED) {
          this.setStatus('reconnecting');
          this.reconnect();
        } else {
          this.setStatus('error');
          this.emitError(new Error('SSE connection error'));
        }
      });

      this.eventSource.addEventListener('open', () => {
        this.setStatus('connected');
        this.reconnectAttempt = 0;
      });

      this.setStatus('connected');
    } catch (error) {
      this.setStatus('error');
      this.emitError(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.setStatus('disconnected');
    this.cleanup();
  }

  async send(_data: string | ArrayBuffer): Promise<void> {
    // SSE is one-way, server->client only
    throw new Error('Cannot send data via SSE transport (server-to-client only)');
  }

  private async reconnect(): Promise<void> {
    this.reconnectAttempt++;

    const delayMs = calculateRetryDelay(this.reconnectAttempt, {
      initialDelayMs: 1000,
      maxDelayMs: 30000,
      backoffMultiplier: 2,
      maxAttempts: 0,
    });

    if (delayMs) {
      await delay(delayMs);
      try {
        await this.connect();
      } catch (error) {
        if (error instanceof Error) {
          this.logger.debug(`SSE reconnect failed (attempt ${this.reconnectAttempt}), will retry`, error);
        }
        this.reconnect();
      }
    }
  }
}
